import {
  generateExcelTemplate,
  getColumnName,
  readFirstSheet,
} from "common/excel";
import * as ids from "common/ids";

/**
 * Config key: `cat_exposure_upload_template` (company-level, editable in admin panel)
 *
 * This config drives the upload dialog, Excel template generation, and column parsing.
 * Each column definition maps a spreadsheet column (by index) to an internal field.
 *
 * Column definition fields:
 *   index         {number}   - 0-based column position in the spreadsheet (required)
 *   header        {string}   - Label shown in the Excel header row and preview table (required)
 *   field         {string}   - Internal Redux field name; omit for display-only columns (e.g. State)
 *   type          {string}   - "number" | "percent" (divides by 100 on import) | omit for string
 *   enumKey       {string}   - Resolves display names → config keys from cat_exposures.enums.
 *                              Use "regionPeril" and it auto-namespaces to regionPeril_{catModel}
 *                              in multi-cat-model mode.
 *   values        {string[]} - Inline dropdown choices. Takes precedence over enumKey.
 *                              Values are stored as-is (no key resolution).
 *   dropdownIndex {number}   - 0-based slot in the DropDowns reference sheet. Required when
 *                              enumKey or values is set, to wire Excel in-cell validation.
 *
 * Top-level template fields:
 *   dataRows      {number}   - Number of empty data rows in the generated template (default: 200).
 *                              Also controls how far down the dropdown validation applies.
 *
 * Example 1 — enum-backed dropdowns (Helix standard config):
 *
 * {
 *   "template": {
 *     "filename": "Cat_Exposure_Upload_Template.xlsx",
 *     "sheetName": "Manual Cat Exposure",
 *     "dropdownSheetName": "DropDowns",
 *     "columns": [
 *       { "index": 0, "header": "State" },
 *       { "index": 1, "header": "Address/City/County/Zip" },
 *       { "index": 2, "header": "Description/Details",  "field": "_DESCRIPTION" },
 *       { "index": 3, "header": "Region/Peril",         "field": "_REGION_PERIL",    "enumKey": "regionPeril",    "dropdownIndex": 0 },
 *       { "index": 4, "header": "Value",                "field": "_VALUE",           "type": "number" },
 *       { "index": 5, "header": "Cat Hazard",           "field": "_CAT_HAZARD",      "enumKey": "catHazard",      "dropdownIndex": 1 },
 *       { "index": 6, "header": "Risk Management",      "field": "_RISK_MANAGEMENT", "enumKey": "riskManagement", "dropdownIndex": 2 },
 *       { "index": 7, "header": "Manual Rate(%)",       "field": "_EXPOSURE_RATE",   "type": "percent" }
 *     ]
 *   }
 * }
 *
 * Example 2 — inline values (no cat_exposures enum needed):
 *
 * {
 *   "template": {
 *     "filename": "My_Template.xlsx",
 *     "sheetName": "Data Entry",
 *     "columns": [
 *       { "index": 0, "header": "Description", "field": "_DESCRIPTION" },
 *       { "index": 1, "header": "Peril", "field": "_REGION_PERIL",
 *         "values": ["Tornado - Low", "Tornado - High", "Flood - Low", "Flood - High"],
 *         "dropdownIndex": 0 },
 *       { "index": 2, "header": "Value", "field": "_VALUE", "type": "number" }
 *     ]
 *   }
 * }
 */

/**
 * Resolves the correct regionPeril enum key depending on the active cat model.
 * In multi-cat-model mode the enum is namespaced as `regionPeril_{catModel}`.
 */
const getRegionPerilEnumKey = (catModel) =>
  catModel ? `regionPeril_${catModel}` : "regionPeril";

const buildReverseMap = (choices) =>
  Object.fromEntries(
    (choices ?? [])
      .filter(({ key, name }) => key && name)
      .map(({ key, name }) => [name, key])
  );

/**
 * Builds name→key reverse maps for each enum field referenced in the upload config.
 * Used during import to resolve display names back to internal config keys.
 *
 * @param {Object} catExposuresConfig - The `cat_exposures` config object from staticDataService
 * @param {Object} uploadConfig - The `cat_exposure_upload_template` config object from staticDataService
 * @param {string|null} catModel - Active cat model key (null in single-model mode)
 * @returns {Object} Map of field name → { displayName: configKey }
 */
export const buildEnumReverseMaps = (
  catExposuresConfig,
  uploadConfig,
  catModel
) => {
  const enums = catExposuresConfig?.enums ?? {};
  const columns = uploadConfig?.template?.columns ?? [];

  return Object.fromEntries(
    columns
      .filter((col) => col.field && (col.enumKey || col.values))
      .map((col) => {
        if (col.values) {
          return [col.field, Object.fromEntries(col.values.map((v) => [v, v]))];
        }
        const enumKey =
          col.enumKey === "regionPeril"
            ? getRegionPerilEnumKey(catModel)
            : col.enumKey;
        return [col.field, buildReverseMap(enums[enumKey]?.choices)];
      })
  );
};

export const parseExposureFile = (file) =>
  readFirstSheet(file, { skipFirstRow: true });

/**
 * Maps raw spreadsheet rows to exposure objects using the column definitions
 * from the `cat_exposure_upload_template` config.
 *
 * Column types:
 *   (none)    → string, stored as-is
 *   "number"  → parsed as a float
 *   "percent" → parsed as float then divided by 100 (e.g. 5 → 0.05)
 *   "enum"    → display name resolved to config key via reverseMap; unresolved values produce warnings
 *
 * @param {Array<Array<*>>} rows - Raw rows from readFirstSheet (header already stripped)
 * @param {Object} reverseMaps - Built via buildEnumReverseMaps
 * @param {Object} uploadConfig - The `cat_exposure_upload_template` config object
 * @returns {{ exposures: Object[], warnings: string[] }}
 */
export const mapRowsToExposures = (rows, reverseMaps, uploadConfig) => {
  const columns = (uploadConfig?.template?.columns ?? []).filter(
    (c) => c.field
  );
  const exposures = [];
  const warnings = [];

  rows.forEach((row, rowIndex) => {
    const exposure = { id: ids.getUniqueKey() };
    let hasData = false;

    columns.forEach(({ index, field, type, enumKey, header }) => {
      const rawValue = row[index];
      if (rawValue == null || String(rawValue).trim() === "") return;

      if (type === "number") {
        const num =
          typeof rawValue === "number" ? rawValue : parseFloat(rawValue);
        if (Number.isFinite(num)) {
          exposure[field] = num;
          hasData = true;
        }
      } else if (type === "percent") {
        const num =
          typeof rawValue === "number" ? rawValue : parseFloat(rawValue);
        if (Number.isFinite(num)) {
          exposure[field] = num / 100;
          hasData = true;
        }
      } else if (enumKey) {
        const displayName = String(rawValue).trim();
        const key = reverseMaps[field]?.[displayName];
        if (key) {
          exposure[field] = key;
          hasData = true;
        } else {
          warnings.push(
            `Row ${rowIndex + 2}: unrecognized ${header} "${displayName}"`
          );
        }
      } else {
        exposure[field] = String(rawValue).trim();
        hasData = true;
      }
    });

    if (hasData) exposures.push(exposure);
  });

  return { exposures, warnings };
};

/**
 * Generates and downloads the cat exposure upload template.
 * Column layout, dropdown lists, and validations are all driven by the
 * `cat_exposure_upload_template` config (editable per company in the admin panel).
 *
 * @param {Object} catExposuresConfig - The `cat_exposures` config (provides enum choice names)
 * @param {Object} uploadConfig - The `cat_exposure_upload_template` config
 * @param {string|null} catModel - Active cat model key (null in single-model mode)
 */
export const generateTemplate = async (
  catExposuresConfig,
  uploadConfig,
  catModel
) => {
  const enums = catExposuresConfig?.enums ?? {};
  const template = uploadConfig?.template ?? {};
  const columns = template.columns ?? [];

  const headers = columns.map((c) => c.header);

  // Build dropdown lists from config columns that reference an enum or define inline values
  const dropdownColumns = columns.filter(
    (c) => (c.enumKey || c.values) && c.dropdownIndex != null
  );
  const dropdownSlots = [];
  dropdownColumns.forEach((col) => {
    const values =
      col.values ??
      (() => {
        const enumKey =
          col.enumKey === "regionPeril"
            ? getRegionPerilEnumKey(catModel)
            : col.enumKey;
        return (enums[enumKey]?.choices ?? []).map((c) => c.name);
      })();
    dropdownSlots[col.dropdownIndex] = { header: col.header, values };
  });
  const dropdowns = dropdownSlots.filter(Boolean);

  // Map dropdown columns to their Excel column letters (A–Z, AA… based on index in headers)
  const validations = dropdownColumns.map((col) => ({
    dataColumn: getColumnName(col.index),
    dropdownIndex: col.dropdownIndex,
  }));

  await generateExcelTemplate({
    filename: template.filename ?? "Cat_Exposure_Upload_Template.xlsx",
    sheets: [
      {
        name: template.sheetName ?? "Manual Cat Exposure",
        headers,
        ...(template.dataRows != null && { dataRows: template.dataRows }),
      },
    ],
    dropdowns,
    dropdownSheetName: template.dropdownSheetName ?? "DropDowns",
    validations,
  });
};
